import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../Components/Button";
import Select from "react-select";
import {
  clearBusinessProduct,
  clearBusinessProductError,
  clearProductCategory,
  requestCreateBusinessProduct,
  requestCreateProductCategory,
  requestGetBusinessProduct,
  requestListBusiness,
  requestListProductCategories,
  requestUpdateBusinessProduct,
  useAuthState,
  useBusinessProductDispatch,
  useBusinessProductState,
  useBusinessTransactionDispatch,
  useBusinessTransactionState,
} from "../../Context";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Alert } from "../../Components/Alert";
import InputSkeleton from "../../Components/InputSkeleton";
import { ModalConfirm } from "../../Components/ModalConfirm";
import { classNames, getErrorMessages } from "../../Utils";
import { NumericFormat } from "react-number-format";
import FormLabel from "../../Components/FormLabel";
import { Modal } from "../../Components/Modal";
import ErrorSelect from "../../Components/ErrorSelect";

export const productTypes = [
  { label: "Goods", value: "GOODS" },
  { label: "Services", value: "SERVICES" },
];

export const ProductCreate = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [selectedBusiness, setselectedBusiness] = useState(null);
  const [productName, setproductName] = useState("");
  const [type, settype] = useState("GOODS");
  const [selectedCategory, setselectedCategory] = useState(null);
  const [price, setprice] = useState({ value: null });
  const [cost, setcost] = useState({ value: null });
  const [reference, setreference] = useState("");
  const [fieldError, setfieldError] = useState(false);
  const [showSuccessModal, setshowSuccessModal] = useState(false);
  const [typeError, settypeError] = useState(null);
  const [priceError, setpriceError] = useState(null);
  const [nameError, setnameError] = useState(null);
  const [submitted, setsubmitted] = useState(false);

  const [showCategoryModal, setshowCategoryModal] = useState(false);
  const [categoryName, setcategoryName] = useState("");
  const [businessError, setbusinessError] = useState(null);
  const [categoryNameError, setcategoryNameError] = useState(null);
  const [categorySubmitted, setcategorySubmitted] = useState(false);

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const businessIdParam = searchParams.get("id");
  const { id: productIdParam } = useParams();

  const businessTransactionDispatch = useBusinessTransactionDispatch();
  const businessProductDispatch = useBusinessProductDispatch();

  const { businesses, businessLoading } = useBusinessTransactionState();

  const {
    newProduct,
    newProductLoading,
    newProductError,
    businessProduct,
    businessProductLoading,
    businessProductError,
    productID,
    updateProductLoading,
    updateProductError,
    productCategories,
    productCategoriesLoading,
    productCategoriesError,
    newCategory,
    newCategoryError,
    newCategoryLoading,
  } = useBusinessProductState();

  const { user } = useAuthState();

  const isFormDisabled = businessProduct?.id;

  useEffect(() => {
    clearBusinessProduct(businessProductDispatch);
    clearBusinessProductError(businessProductDispatch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user.id) {
      requestListBusiness(businessTransactionDispatch, {
        owner: user.id,
        page: 1,
        limit: 10,
      });
    }
  }, [businessTransactionDispatch, user.id]);

  useEffect(() => {
    if (productIdParam) {
      requestGetBusinessProduct(businessProductDispatch, {
        id: productIdParam,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productIdParam]);

  useEffect(() => {
    if (businessIdParam || selectedBusiness) {
      requestListProductCategories(businessProductDispatch, {
        businessID: businessIdParam || selectedBusiness.id,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessIdParam, selectedBusiness]);

  useEffect(() => {
    if (businessProduct?.id && businessProduct?.businessID) {
      const { name, type, price, cost, reference, category } = businessProduct;

      setproductName(name);
      settype(type);
      setprice({ value: price });
      setcost({ value: cost ?? null });

      if (reference) {
        setreference(reference);
      }

      if (category) {
        setselectedCategory({
          label: category?.name,
          value: category?.id,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessProduct?.id]);

  useEffect(() => {
    if (businesses.length > 0) {
      if (businessIdParam) {
        const business = businesses.find(
          (item) => item.value === businessIdParam || businessProduct.id
        );
        if (business) {
          setselectedBusiness({
            ...business,
            label: business.name,
            value: business.id,
          });
        }
      } else if (productIdParam) {
        const business = businesses.find(
          (item) => item.value === businessProduct.businessID
        );
        if (business) {
          setselectedBusiness({
            ...business,
            label: business.name,
            value: business.id,
          });
        }
      } else {
        setselectedBusiness(businesses[0]);
      }
    }
  }, [businessIdParam, productIdParam, businesses, businessProduct]);

  useEffect(() => {
    if (newProduct || productID) {
      setshowSuccessModal(true);
    }
  }, [newProduct, productID]);

  useEffect(() => {
    if (submitted) {
      const isBusinessValid = Boolean(selectedBusiness?.value);
      const isNameValid = productName.length >= 1;
      if (isNameValid) {
        setErrorMessage("name", "");
      } else {
        setErrorMessage("name", "Product name is required.");
      }
      if (isBusinessValid) {
        setErrorMessage("business", "");
      } else {
        setErrorMessage("business", "Please select business first");
      }
    }
  }, [selectedBusiness, productName, submitted]);

  useEffect(() => {
    if (submitted && price) {
      const isPriceValid = Boolean(price.value);
      if (isPriceValid) {
        setErrorMessage("price", "");
      } else {
        setErrorMessage("price", "Price is required.");
      }
    }
  }, [price, submitted]);

  useEffect(() => {
    if (submitted && type) {
      const isTypeValid = productTypes.some(
        (productType) => productType.value === type
      );
      if (isTypeValid) {
        setErrorMessage("type", "");
      } else {
        setErrorMessage("type", "Please select a valid type.");
      }
    }
  }, [type, submitted]);

  useEffect(() => {
    if (categorySubmitted) {
      const isNameValid = categoryName.length >= 1;
      const isBusinessValid = Boolean(selectedBusiness?.value);
      if (isNameValid) {
        setErrorMessage("categoryName", "");
      } else {
        setErrorMessage("categoryName", "Category name is required");
      }
      if (isBusinessValid) {
        setErrorMessage("business", "");
      } else {
        setErrorMessage("business", "Please select business first");
      }
    }
  }, [selectedBusiness, categoryName, categorySubmitted]);

  useEffect(() => {
    if (newCategory) {
      setshowCategoryModal(false);
      setshowSuccessModal(true);
      if (newCategory.id && newCategory.name) {
        setselectedCategory({
          label: newCategory.name,
          value: newCategory.id,
        });
      }
    }
  }, [newCategory]);

  const validateInput = () => {
    let currentValidBusiness = true;
    let currentValidName = true;
    let currentValidPrice = true;
    let currentValidType = true;

    const isBusinessValid = Boolean(selectedBusiness?.value);
    const isPriceValid = Boolean(price.value);
    const isNameValid = productName.length >= 1;
    const isTypeValid = productTypes.some(
      (productType) => productType.value === type
    );

    currentValidBusiness = isBusinessValid;
    currentValidName = isNameValid;
    currentValidPrice = isPriceValid;
    currentValidType = isTypeValid;

    return (
      currentValidBusiness &&
      currentValidName &&
      currentValidPrice &&
      currentValidType
    );
  };

  const validateCategoryInput = () => {
    let currentValidCategoryName = true;
    let currentValidBusiness = true;

    const isNameValid = categoryName.length >= 1;
    const isBusinessValid = Boolean(selectedBusiness?.value);

    currentValidCategoryName = isNameValid;
    currentValidBusiness = isBusinessValid;

    return currentValidBusiness && currentValidCategoryName;
  };

  const onSave = (update = false) => {
    setsubmitted(true);
    const validForm = validateInput();

    if (
      (businessIdParam || selectedBusiness?.value) &&
      productName &&
      type &&
      price.value
    ) {
      setfieldError(false);
    } else {
      setfieldError(true);
      return;
    }

    if (validForm) {
      const input = {
        businessID: businessIdParam || selectedBusiness.value,
        name: productName,
        type,
        price: price.value,
        cost: cost.value ? Number(cost.value) : null,
        reference,
      };

      if (selectedCategory?.value) {
        input.categoryID = selectedCategory.value;
      } else {
        input.categoryID = null;
      }

      if (update) {
        input.id = businessProduct.id;
        requestUpdateBusinessProduct(businessProductDispatch, { input });
      } else {
        requestCreateBusinessProduct(businessProductDispatch, { input });
      }
    }
  };

  const handleCategorySave = () => {
    setcategorySubmitted(true);
    if (validateCategoryInput()) {
      const input = {
        businessID: selectedBusiness.id || businessIdParam,
        name: categoryName,
      };
      requestCreateProductCategory(businessProductDispatch, { input });
    }
  };

  const setErrorMessage = (field, message) => {
    switch (field) {
      case "type":
        settypeError(message);
        break;
      case "price":
        setpriceError(message);
        break;
      case "name":
        setnameError(message);
        break;
      case "categoryName":
        setcategoryNameError(message);
        break;
      case "business":
        setbusinessError(message);
        break;
      default:
        break;
    }
  };

  const navigateToProduct = () => {
    setshowSuccessModal(false);
    if (!newProductError) {
      if (newProduct) {
        navigate(`/product?id=${businessIdParam}`);
        clearBusinessProduct(businessProductDispatch);
      }
    }
    if (!updateProductError) {
      if (productID) {
        navigate(`/product?id=${selectedBusiness.value}`);
        clearBusinessProduct(businessProductDispatch);
      }
    }
  };

  const renderCategoryModal = () => {
    return (
      <Modal
        title={t("Add Category")}
        show={showCategoryModal}
        onClose={() => {
          setshowCategoryModal(false);
          setcategorySubmitted(false);
          setcategoryName("");
          setcategoryNameError("");
          setbusinessError("");
          clearProductCategory(businessProductDispatch);
        }}
        footer={
          <div className="p-4 flex justify-end items-center bg-gray-50 rounded-b-lg">
            <Button
              disabled={newCategoryLoading}
              loading={newCategoryLoading}
              onClick={(e) => {
                e.preventDefault();
                handleCategorySave();
              }}
              className="w-24 shrink-0"
              title="Save"
            />
          </div>
        }
      >
        <div className="flex flex-col gap-4 px-4 py-5">
          {(newCategoryError || businessError) && (
            <Alert
              title="Error"
              type="error"
              message={
                newCategoryError?.message ||
                businessError ||
                "newCategoryError delete Transaction."
              }
              errors={newCategoryError?.errors}
            />
          )}
          <div>
            <FormLabel htmlFor="name" label={t("Name")} required />
            <input
              type="text"
              name="name"
              value={categoryName}
              placeholder={t("Enter Category Name")}
              onChange={(e) => setcategoryName(e.target.value)}
              className={classNames(
                categoryNameError
                  ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                  : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                "mt-1 block w-full text-sm rounded-md shadow-sm"
              )}
            />
            {categoryNameError && (
              <div className="mt-1 text-red-600 text-sm">
                {t(categoryNameError)}
              </div>
            )}
          </div>
        </div>
      </Modal>
    );
  };

  return (
    <div className="bg-white border border-gray-200 divide-y divide-gray-200 shadow sm:overflow-hidden rounded-md sm:rounded-lg">
      {renderCategoryModal()}
      {newCategory && (
        <ModalConfirm
          open={showSuccessModal}
          setOpen={(open) => {
            setshowSuccessModal(open);
          }}
          title="Success"
          description={"Your new category has been created."}
          type="SUCCESS"
          onConfirm={() => {
            setshowCategoryModal(false);
            setshowSuccessModal(false);
            clearProductCategory(businessProductDispatch);
            setcategorySubmitted(false);
            setcategoryName("");
            setcategoryNameError("");
            setbusinessError("");
          }}
        />
      )}
      <form onSubmit={onSave}>
        <div className="space-y-6 px-4 py-6 sm:p-6">
          {(newProduct || productID) && (
            <ModalConfirm
              open={showSuccessModal}
              setOpen={(open) => {
                setshowSuccessModal(open);
              }}
              title="Success"
              description={
                productIdParam
                  ? "The product has been successfully changed"
                  : "Your new product has been created."
              }
              type="SUCCESS"
              onConfirm={navigateToProduct}
            />
          )}
          {fieldError && (
            <Alert
              title="Incomplete Form"
              type="warning"
              message="Please fill out all fields."
            />
          )}
          {(newProductError || updateProductError) && (
            <Alert
              title="Error"
              type="error"
              message={
                t(getErrorMessages(newProductError || updateProductError)) ||
                "Error create Product."
              }
              errors={newProductError?.errors || updateProductError?.errors}
            />
          )}
          {businessProductError && (
            <Alert
              title="Error"
              type="error"
              message={
                t(getErrorMessages(businessProductError)) ||
                "Error get Product."
              }
              errors={businessProductError?.errors}
            />
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {newProductLoading ||
            businessProductLoading ||
            updateProductLoading ? (
              <InputSkeleton rows={3} />
            ) : (
              <>
                <div>
                  <FormLabel
                    htmlFor="business"
                    label={t("Business Name")}
                    required
                  />
                  <Select
                    name="business"
                    className={classNames(
                      businessError
                        ? "border-red-500 rounded border focus:border-red-500 focus:ring-red-500"
                        : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                      "mt-1 text-sm"
                    )}
                    classNamePrefix="select2-selection"
                    options={businesses}
                    isLoading={businessLoading}
                    isDisabled={businessLoading || isFormDisabled}
                    placeholder={t("Select Business Name")}
                    value={selectedBusiness || ""}
                    onChange={setselectedBusiness}
                  />
                  {businessError && (
                    <div className="mt-1 text-red-600 text-sm">
                      {t(businessError)}
                    </div>
                  )}
                </div>
                <div>
                  <FormLabel htmlFor="type" label={t("Type")} required />
                  <select
                    id="type"
                    name="type"
                    value={type}
                    onChange={(e) => settype(e.target.value)}
                    className={classNames(
                      typeError
                        ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                        : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                      "mt-1 block w-full text-sm rounded-md shadow-sm disabled:bg-[#F2F2F2] disabled:text-gray-600"
                    )}
                    disabled={isFormDisabled}
                  >
                    {productTypes.map((productType) => (
                      <option key={productType.value} value={productType.value}>
                        {t(productType.label)}
                      </option>
                    ))}
                  </select>
                  {typeError && (
                    <div className="mt-1 text-red-600 text-sm">
                      {t(typeError)}
                    </div>
                  )}
                </div>
                <div>
                  <FormLabel htmlFor="name" label={t("Name")} required />
                  <input
                    type="text"
                    name="name"
                    value={productName}
                    placeholder={t("Enter Product Name")}
                    maxLength={120}
                    onChange={(e) => setproductName(e.target.value)}
                    className={classNames(
                      nameError
                        ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                        : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                      "mt-1 block w-full text-sm rounded-md shadow-sm"
                    )}
                  />
                  {nameError && (
                    <div className="mt-1 text-red-600 text-sm">
                      {t(nameError)}
                    </div>
                  )}
                </div>
                {productCategoriesError ? (
                  <ErrorSelect
                    name="category"
                    label="Category"
                    error={productCategoriesError}
                    addButtonTitle="Add Category"
                    onClickAddButton={() => {
                      setshowCategoryModal(true);
                    }}
                    onRefresh={() => {
                      if (businessIdParam || selectedBusiness) {
                        requestListProductCategories(businessProductDispatch, {
                          page: 1,
                          businessID: businessIdParam || selectedBusiness.id,
                        });
                      }
                    }}
                  />
                ) : (
                  <div>
                    <div className="flex justify-between">
                      <FormLabel htmlFor="category" label={t("Category")} />
                      {(businessIdParam || selectedBusiness) && (
                        <button
                          type="button"
                          onClick={() => {
                            setshowCategoryModal(true);
                          }}
                          className="text-blue-600 text-sm font-medium"
                        >
                          {t("Add Category")}
                        </button>
                      )}
                    </div>
                    <Select
                      id="category"
                      className="mt-1 text-sm"
                      classNamePrefix="select2-selection"
                      options={productCategories.map((item) => ({
                        value: item.id,
                        label: item.name,
                      }))}
                      value={selectedCategory}
                      onChange={(selectedOption) =>
                        setselectedCategory(selectedOption)
                      }
                      placeholder={t("Select Category")}
                      maxMenuHeight={150}
                      menuPortalTarget={document.body}
                      isLoading={productCategoriesLoading}
                      isDisabled={productCategoriesLoading}
                      isClearable
                    />
                  </div>
                )}

                <div>
                  <FormLabel htmlFor="price" label={t("Price")} required />
                  <NumericFormat
                    type="text"
                    name="price"
                    id="price"
                    value={price.value || ""}
                    placeholder={t("Enter Price")}
                    thousandSeparator="."
                    decimalSeparator=","
                    displayType="input"
                    prefix={"Rp"}
                    onValueChange={(values) => setprice(values)}
                    className={classNames(
                      priceError
                        ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                        : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                      "mt-1 block w-full text-sm rounded-md shadow-sm disabled:bg-[#F2F2F2] disabled:text-gray-400"
                    )}
                    disabled={isFormDisabled}
                  />
                  {priceError && (
                    <div className="mt-1 text-red-600 text-sm">
                      {t(priceError)}
                    </div>
                  )}
                </div>
                <div>
                  <FormLabel htmlFor="cost" label={t("Cost")} />
                  <NumericFormat
                    type="text"
                    name="cost"
                    id="cost"
                    value={cost.value || ""}
                    placeholder={t("Enter Cost")}
                    thousandSeparator="."
                    decimalSeparator=","
                    displayType="input"
                    prefix={"Rp"}
                    onValueChange={(values) => setcost(values)}
                    className={classNames(
                      "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                      "mt-1 block w-full text-sm rounded-md shadow-sm disabled:bg-[#F2F2F2] disabled:text-gray-400"
                    )}
                    disabled={isFormDisabled}
                  />
                </div>
                <div>
                  <FormLabel
                    htmlFor="reference"
                    label={t("Reference Number")}
                  />
                  <input
                    type="text"
                    name="reference"
                    value={reference}
                    placeholder={t("Enter Reference Number")}
                    maxLength={120}
                    onChange={(e) => setreference(e.target.value)}
                    className="border-gray-300 focus:border-blue-500 focus:ring-blue-500 mt-1 block w-full text-sm rounded-md shadow-sm"
                  />
                </div>
              </>
            )}
          </div>
        </div>
        <div className="bg-gray-50 rounded-b-md sm:rounded-b-lg px-4 py-6 text-right sm:px-6">
          <Button
            type="submit"
            className="w-full sm:w-auto"
            title={productIdParam ? t("Change Product") : t("Create Product")}
            loading={newProductLoading || updateProductLoading}
            disabled={
              newProductLoading ||
              Boolean(productName === "") ||
              price.value === null ||
              updateProductLoading
            }
            onClick={(e) => {
              e.preventDefault();
              productIdParam ? onSave(true) : onSave();
            }}
          />
        </div>
      </form>
    </div>
  );
};
